package com.example.mystybag.appCore.model.pages

import com.example.mystybag.login.model.MerchantBusinessCategory

/**
 * Configuration for review rating categories based on business category.
 * Each business category has 4 rating criteria.
 */
data class ReviewCategoryConfig(
    val businessCategory: MerchantBusinessCategory,
    val ratingCategories: List<ReviewRatingCategory>
)

data class ReviewRatingCategory(
    val key: String, // Unique key for the rating (e.g., "quality", "service", etc.)
    val labelKey: String // String resource key for the label
)

object ReviewCategoryConfigs {
    /**
     * Get review rating categories for a specific business category.
     * Each category has exactly 4 rating criteria.
     */
    fun getCategoriesForBusinessCategory(businessCategoryId: Long?): List<ReviewRatingCategory> {
        val category = MerchantBusinessCategory.fromIdOrNull(businessCategoryId)
            ?: return getDefaultCategories()
        
        return when (category) {
            MerchantBusinessCategory.BAKERY -> listOf(
                ReviewRatingCategory("freshness", "review_rating_freshness"),
                ReviewRatingCategory("quality", "review_rating_quality"),
                ReviewRatingCategory("variety", "review_rating_variety"),
                ReviewRatingCategory("value", "review_rating_value")
            )
            MerchantBusinessCategory.CONFECTIONERY -> listOf(
                ReviewRatingCategory("taste", "review_rating_taste"),
                ReviewRatingCategory("quality", "review_rating_quality"),
                ReviewRatingCategory("variety", "review_rating_variety"),
                ReviewRatingCategory("presentation", "review_rating_presentation")
            )
            MerchantBusinessCategory.FOOD_FOR_HOME -> listOf(
                ReviewRatingCategory("taste", "review_rating_taste"),
                ReviewRatingCategory("quality", "review_rating_quality"),
                ReviewRatingCategory("portion_size", "review_rating_portion_size"),
                ReviewRatingCategory("packaging_transport", "review_rating_packaging_transport")
            )
            MerchantBusinessCategory.FISH_MARKET -> listOf(
                ReviewRatingCategory("freshness", "review_rating_freshness"),
                ReviewRatingCategory("quality", "review_rating_quality"),
                ReviewRatingCategory("variety", "review_rating_variety"),
                ReviewRatingCategory("value", "review_rating_value")
            )
        }
    }
    
    /**
     * Default categories if business category is not found or not specified.
     * These are the original 4 categories.
     */
    private fun getDefaultCategories(): List<ReviewRatingCategory> {
        return listOf(
            ReviewRatingCategory("collection_experience", "collection_experience"),
            ReviewRatingCategory("food_quality", "food_quality"),
            ReviewRatingCategory("variety_of_contents", "variety_of_contents"),
            ReviewRatingCategory("food_quantity", "food_quantity")
        )
    }
}
